import { useMemo } from 'react';
import { IPathwayActivitiesResponse, IPathwayActivity } from 'app/_modules/pathways/entities';
import { IProcessedMeasurement } from 'app/types/measurement';

type TimePeriod = 'morning' | 'afternoon' | 'evening';

const getTimePeriodFromTimestamp = (timestamp: string): TimePeriod => {
  const hour = new Date(timestamp).getHours();
  if (isNaN(hour) || hour >= 18) return 'evening';
  if (hour >= 12) return 'afternoon';
  if (hour >= 6) return 'morning';
  return 'evening';
};

const toPathwayActivity = (m: IProcessedMeasurement): IPathwayActivity => ({
  activityId: m.activityId ?? m.id,
  activityType: m.activityType as IPathwayActivity['activityType'],
  activityName: m.activityName as IPathwayActivity['activityName'],
  status: 'completed',
  completedAt: m.measuredAt,
  standalone: m.standalone,
  measurement: m.rawMeasurement,
});

export const useMergedActivities = (
  activities: IPathwayActivitiesResponse | undefined,
  wsMeasurements: IProcessedMeasurement[],
): IPathwayActivitiesResponse | undefined => {
  return useMemo(() => {
    if (!activities) return undefined;
    if (wsMeasurements.length === 0) return activities;

    const existingMeasurementIds = new Set<string>();
    const periods: TimePeriod[] = ['morning', 'afternoon', 'evening'];

    for (const p of periods) {
      for (const activity of activities[p]) {
        if (activity.measurement?.id) {
          existingMeasurementIds.add(activity.measurement.id);
        }
      }
    }
    for (const activity of activities.daily) {
      if (activity.measurement?.id) {
        existingMeasurementIds.add(activity.measurement.id);
      }
    }

    const newMeasurements = wsMeasurements.filter(
      m => !existingMeasurementIds.has(m.id),
    );

    if (newMeasurements.length === 0) return activities;

    const merged: IPathwayActivitiesResponse = {
      date: activities.date,
      daily: [...activities.daily],
      morning: [...activities.morning],
      afternoon: [...activities.afternoon],
      evening: [...activities.evening],
    };

    const completeMergedActivity = (
      list: IPathwayActivity[],
      m: IProcessedMeasurement,
    ): boolean => {
      if (!m.activityId) return false;
      const idx = list.findIndex(
        a => a.activityId === m.activityId && a.status !== 'completed',
      );
      if (idx === -1) return false;
      list[idx] = { ...list[idx], status: 'completed', completedAt: m.measuredAt, measurement: m.rawMeasurement };
      return true;
    };

    for (const m of newMeasurements) {
      const period = getTimePeriodFromTimestamp(m.measuredAt);

      const replaced =
        completeMergedActivity(merged[period], m) ||
        completeMergedActivity(merged.daily, m) ||
        periods.filter(p => p !== period).some(p => completeMergedActivity(merged[p], m));

      if (!replaced) {
        merged[period].push(toPathwayActivity(m));
      }
    }

    return merged;
  }, [activities, wsMeasurements]);
};
